// ── resource-form-modal: the create/edit resource Modal (Wave 8) ────────────
// One @vjal/ui Modal that backs both the create flow (POST /resources) and the
// edit flow (PATCH /resources/:id) — the `mode` prop only changes the title, the
// submit label, and the initial values. Presentational + validation-only; the
// server is authoritative (staff/super-admin only for mutations — the server
// re-checks and 403s, which we render honestly inline).
//
// The backend `resourceType` is a free-form string; the form offers the three
// product categories as a convenience but never invents fields beyond
// title/description/resourceType/url/isPublic (the documented create/update body).
//
// Honest write contract: a server rejection (validation / permission-denied /
// gate-closed / unavailable) is surfaced INLINE via describeResourceWriteError —
// never swallowed, never faked. The caller (the page) owns the actual mutation
// and closes + toasts ONLY after the backend confirms success.

import { useEffect, useId, useRef, useState } from "react";
import { Alert, Button, Checkbox, Input, Modal, Select, Textarea } from "@vjal/ui";

import type { CoachingRecordInput, CoachingResourceDto } from "../../services/coaching-dtos";
import {
  RESOURCE_CATEGORIES,
  resourceCategoryLabels,
  resourceCategoryOf
} from "./resources-helpers";
import { describeResourceWriteError } from "./resources-write";

export interface ResourceFormValues {
  title: string;
  description: string;
  resourceType: string;
  url: string;
  isPublic: boolean;
}

export interface ResourceFormModalProps {
  open: boolean;
  mode: "create" | "edit";
  resource?: CoachingResourceDto | null;
  onClose: () => void;
  onSubmit: (values: ResourceFormValues) => Promise<unknown>;
  submitting?: boolean;
  submitError?: unknown;
}

/**
 * Explicit write-body projection (class-9 guard): only the validator's accepted
 * keys (validators.resource: title, description, resourceType, url, isPublic) —
 * never a spread, so future form-local state can't leak into POST/PATCH bodies.
 */
export function resourceWriteBody(values: ResourceFormValues): CoachingRecordInput {
  return {
    title: values.title,
    description: values.description,
    resourceType: values.resourceType,
    url: values.url,
    isPublic: values.isPublic
  };
}

interface FieldErrors {
  title?: string;
  url?: string;
}

function initialValues(mode: "create" | "edit", resource?: CoachingResourceDto | null): ResourceFormValues {
  if (mode === "edit" && resource) {
    return {
      title: resource.title ?? "",
      description: resource.description ?? "",
      resourceType: resource.resource_type ?? resourceCategoryOf(resource),
      url: resource.url ?? "",
      isPublic: Boolean(resource.is_public)
    };
  }
  return { title: "", description: "", resourceType: "documentation", url: "", isPublic: true };
}

export function ResourceFormModal({
  open,
  mode,
  resource,
  onClose,
  onSubmit,
  submitting = false,
  submitError
}: ResourceFormModalProps) {
  const [values, setValues] = useState<ResourceFormValues>(() => initialValues(mode, resource));
  const [errors, setErrors] = useState<FieldErrors>({});
  const formId = useId();

  const wasOpen = useRef(false);
  const seededFor = useRef<string | null>(null);
  useEffect(() => {
    const resourceKey = resource?.id ?? null;
    if (open && (!wasOpen.current || seededFor.current !== resourceKey)) {
      setValues(initialValues(mode, resource));
      setErrors({});
      seededFor.current = resourceKey;
    }
    wasOpen.current = open;
  }, [open, mode, resource]);

  const set = <K extends keyof ResourceFormValues>(key: K, value: ResourceFormValues[K]) =>
    setValues((current) => ({ ...current, [key]: value }));

  function validate(): FieldErrors {
    const next: FieldErrors = {};
    if (!values.title.trim()) next.title = "Resource title is required.";
    if (!values.url.trim()) next.url = "A resource URL is required.";
    return next;
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    const localErrors = validate();
    if (Object.keys(localErrors).length > 0) {
      setErrors(localErrors);
      return;
    }
    setErrors({});
    try {
      await onSubmit({
        title: values.title.trim(),
        description: values.description.trim(),
        resourceType: values.resourceType.trim() || "program_resource",
        url: values.url.trim(),
        isPublic: values.isPublic
      });
    } catch {
      // The owning mutation surfaces the error via `submitError`; keep the modal
      // open with the inline error shown.
    }
  }

  const topError = describeResourceWriteError(submitError);

  return (
    <Modal open={open} onClose={onClose} title={mode === "create" ? "Add resource" : "Edit resource"}>
      <form id={formId} onSubmit={(event) => void handleSubmit(event)} className="flex flex-col gap-4" noValidate>
        {topError && <Alert variant={topError.tone === "warning" ? "warning" : "error"}>{topError.message}</Alert>}

        <Input
          label="Resource title"
          value={values.title}
          onChange={(event) => set("title", event.target.value)}
          error={errors.title}
          required
        />

        <Select
          label="Category"
          value={values.resourceType}
          onChange={(event) => set("resourceType", event.target.value)}
          hint="Reference videos are view-only for coaches; documentation and program resources may be downloaded where allowed."
        >
          {RESOURCE_CATEGORIES.map((category) => (
            <option key={category} value={category}>
              {resourceCategoryLabels[category]}
            </option>
          ))}
        </Select>

        <Input
          label="Resource URL"
          value={values.url}
          onChange={(event) => set("url", event.target.value)}
          error={errors.url}
          placeholder="https://…"
          required
        />

        <Textarea
          label="Description"
          value={values.description}
          onChange={(event) => set("description", event.target.value)}
          rows={3}
        />

        <Checkbox
          label="Global (visible to all roles)"
          checked={values.isPublic}
          onChange={(event) => set("isPublic", event.target.checked)}
        />

        <div className="mt-2 flex items-center justify-end gap-3">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" isLoading={submitting}>
            {mode === "create" ? "Create resource" : "Save changes"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
